/**
 * UpSurv 2.0 — redeeming a scanned onboarding code (DECISIONS D-013).
 *
 * ── Why this is not a method on ApiClient ───────────────────────────────────
 * `ApiClient` refuses before it sends when there is no token — correctly, since
 * every route it speaks to is surveyor-authenticated. This call is the one that
 * happens BEFORE a token exists; a device redeeming a code holds nothing at all,
 * which is the entire problem the QR solves. Bolting it onto that class would
 * mean carving an exception into the guard that keeps unauthenticated requests
 * from leaving, and that guard is worth more than the shared plumbing.
 *
 * It is not on `AdminClient` either: that one signs everything with the office
 * key, which no field phone has and none should.
 *
 * So it is a plain function with no credential of its own. The secret in the
 * body IS the credential, it is used once, and it is never written to storage —
 * only the token it is exchanged for is.
 */

/** What the server hands back once a code has been redeemed. */
export interface OnboardingRedemption {
  /**
   * The surveyor's freshly minted access token. Written to this device by the
   * caller and never rendered, logged or copied anywhere else.
   */
  token: string;
  surveyor: { id: string; name: string };
}

/**
 * Everything a redemption can fail with, in one shape.
 *
 * `code` is the API's own `error` string, plus `offline` when the request never
 * left the device — the distinction the onboarding screen has to draw, because
 * "no signal in this office" and "this code was already used" call for opposite
 * responses from the person standing there.
 */
export class OnboardingError extends Error {
  readonly status: number;
  readonly code: string;

  constructor(message: string, options: { status: number; code: string; cause?: unknown }) {
    super(message, options.cause === undefined ? undefined : { cause: options.cause });
    this.name = 'OnboardingError';
    this.status = options.status;
    this.code = options.code;
  }
}

export interface RedeemOptions {
  baseUrl?: string;
  fetchImpl?: typeof fetch;
}

/**
 * Exchange a scanned secret for this surveyor's access token.
 *
 * One request, no retry. A retry is exactly the wrong instinct here: the code
 * is single-use, so a second attempt after an ambiguous first one is refused as
 * `onboarding_link_used` — the truthful answer, and the reason the screen offers
 * "ask the office for a new code" rather than a Try Again button.
 */
export async function redeemOnboardingSecret(
  secret: string,
  options: RedeemOptions = {},
): Promise<OnboardingRedemption> {
  return redeem({ secret }, options);
}

/**
 * Exchange a TYPED pairing code for this surveyor's access token (D-019).
 *
 * The same endpoint, the same single-use record, a different representation.
 * The code is sent as the surveyor typed it — separators, case and all — and
 * normalised server-side, so the device and the server can never disagree about
 * what `bcd fgh-jkl` means.
 *
 * Same no-retry rule, for the same reason. A typed code that was accepted and
 * whose response was lost is spent; typing it again is correctly refused, and
 * the screen has to say "ask the office for a new one" rather than loop.
 */
export async function redeemPairCode(
  code: string,
  options: RedeemOptions = {},
): Promise<OnboardingRedemption> {
  return redeem({ code }, options);
}

async function redeem(
  presented: { secret: string } | { code: string },
  options: RedeemOptions,
): Promise<OnboardingRedemption> {
  const baseUrl = (options.baseUrl ?? '/api').replace(/\/$/, '');
  const fetchImpl = options.fetchImpl ?? ((...args: Parameters<typeof fetch>) =>
    globalThis.fetch(...args));

  let response: Response;
  try {
    response = await fetchImpl(`${baseUrl}/onboarding/redeem`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(presented),
    });
  } catch (cause) {
    throw new OnboardingError('No connection to the office server.', {
      status: 0,
      code: 'offline',
      cause,
    });
  }

  if (!response.ok) {
    let code = 'http_error';
    let message = '';
    try {
      const parsed = (await response.json()) as { error?: string; message?: string };
      if (parsed.error) code = parsed.error;
      message = parsed.message ?? '';
    } catch {
      /* a body that will not parse adds nothing */
    }
    throw new OnboardingError(message || `Setup failed (${String(response.status)}).`, {
      status: response.status,
      code,
    });
  }

  let body: { token?: unknown; surveyor?: { id?: unknown; name?: unknown } };
  try {
    body = (await response.json()) as typeof body;
  } catch (cause) {
    throw new OnboardingError('The server sent something that was not JSON.', {
      status: response.status,
      code: 'bad_response',
      cause,
    });
  }

  if (
    typeof body.token !== 'string' ||
    typeof body.surveyor?.id !== 'string' ||
    typeof body.surveyor.name !== 'string'
  ) {
    throw new OnboardingError('The server sent an answer this app did not understand.', {
      status: response.status,
      code: 'bad_response',
    });
  }

  return { token: body.token, surveyor: { id: body.surveyor.id, name: body.surveyor.name } };
}
